import React, { useState, useEffect } from 'react';
import { useGetInfluencerSalesReportQuery } from '../../api/influencerCouponApi';
import { useAuthState } from '../../hooks/useAuthState';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import Navbar from '../Navbar/Navbar';
import Footer from '../footer/Footer';

const InfluencerSalesReport = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authState = useAuthState();
    const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString());
    const [endDate, setEndDate] = useState(new Date().toISOString());
    const [authChecked, setAuthChecked] = useState(false);
    const [isAuthorized, setIsAuthorized] = useState(false);
    
    // Check if user has Influencer role - with better handling
    useEffect(() => {
        console.log("Auth State Changed:", authState);
        
        // Only proceed with auth check if we have the necessary properties
        if (authState && (authState.role !== undefined)) {
            setAuthChecked(true);
            
            // Check if user has Influencer role
            const hasInfluencerRole = authState.role && 
                (Array.isArray(authState.role) ? 
                    authState.role.includes("Influencer") : 
                    authState.role === "Influencer" || authState.role.includes("Influencer"));
            
            console.log("Has Influencer role (updated check):", hasInfluencerRole);
            
            if (hasInfluencerRole) {
                setIsAuthorized(true);
            } else {
                setIsAuthorized(false);
                // Only navigate if we're sure the user is unauthorized
                if (authState.nameIdentifier) {
                    navigate('/unauthorized');
                }
            }
        }
    }, [authState, navigate]);

    // Debug: Log user role to console
    useEffect(() => {
        console.log("User role (raw):", authState.role);
        if (authState.role) {
            console.log("Role type:", typeof authState.role);
            if (Array.isArray(authState.role)) {
                console.log("Roles as array:", authState.role);
                console.log("Has Influencer in array:", authState.role.includes("Influencer"));
            } else if (typeof authState.role === 'string') {
                console.log("Role as string:", authState.role);
                console.log("Has Influencer in string:", authState.role.includes("Influencer"));
            }
        }
    }, [authState]);
    
    // Only make the API call if the user is authorized
    const { data: salesReport, isLoading, error } = useGetInfluencerSalesReportQuery({
        influencerId: authState.nameIdentifier,
        startDate,
        endDate
    }, {
        skip: !isAuthorized || !authState.nameIdentifier
    });

    console.log("Auth checked:", authChecked, "Is authorized:", isAuthorized);
    
    // Log detailed API response
    useEffect(() => {
        if (salesReport) {
            console.log("Sales Report API Response:", salesReport);
            console.log("Result structure:", salesReport.result);
            
            if (salesReport.result && salesReport.result.couponSales) {
                console.log("CouponSales array length:", salesReport.result.couponSales.length);
                
                if (salesReport.result.couponSales.length > 0) {
                    console.log("First coupon sale item:", salesReport.result.couponSales[0]);
                    console.log("All properties of first sale:", Object.keys(salesReport.result.couponSales[0]));
                    
                    // Check if there are nested arrays within each sale
                    const firstSale = salesReport.result.couponSales[0];
                    Object.keys(firstSale).forEach(key => {
                        if (Array.isArray(firstSale[key])) {
                            console.log(`Found array in property '${key}':`, firstSale[key]);
                        }
                    });
                }
            }
        }
    }, [salesReport]);

    const handleStartDateChange = (e) => {
        setStartDate(new Date(e.target.value).toISOString());
    };

    const handleEndDateChange = (e) => {
        setEndDate(new Date(e.target.value).toISOString());
    };

    // Show loading state if we're still checking auth or loading data
    if (!authChecked || (isAuthorized && isLoading)) {
        return (
            <>
                <Navbar />
                <div className="container py-5">
                    <div className="text-center py-5">
                        <div className="spinner-border" role="status"></div>
                        <p className="mt-2">{t('Loading your sales report...')}</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Show unauthorized directly in component instead of redirecting
    if (authChecked && !isAuthorized) {
        return (
            <>
                <Navbar />
                <div className="container py-5">
                    <div className="alert alert-danger">
                        <h4>{t('Unauthorized Access')}</h4>
                        <p>{t('You do not have permission to view this page. This page is only accessible to users with the Influencer role.')}</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Show API error
    if (error) {
        return (
            <>
                <Navbar />
                <div className="container py-5">
                    <div className="alert alert-danger">
                        <h4>{t('Error')}</h4>
                        <p>{t('An error occurred while loading your sales report')}</p>
                        <p className="text-muted small">{error.message || JSON.stringify(error)}</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return format(new Date(dateString), 'dd.MM.yyyy');
        } catch (error) {
            console.error("Invalid date format:", dateString, error);
            return '-';
        }
    };

    return (
        <>
        <Navbar />
        <div className="container py-5">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <div>
                            <h2 className="display-5 fw-bold text-primary mb-0">{t('Influencer Sales Report')}</h2>
                            <div className="border-bottom border-primary mt-2" style={{ width: '80px', height: '4px', backgroundColor: '#0d6efd' }}></div>
                        </div>
                        <div className="bg-light p-3 rounded text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-graph-up text-primary me-2" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M0 0h1v15h15v1H0V0Zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07Z"/>
                            </svg>
                            <span className="text-secondary">{t('Financial Insights')}</span>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="card mb-4 shadow-sm">
                <div className="card-body">
                    <div className="row align-items-center mb-3">
                        <div className="col-sm-12 col-md-2">
                            <label htmlFor="startDate" className="form-label">{t('Start Date')}</label>
                        </div>
                        <div className="col-sm-12 col-md-4 mb-3 mb-md-0">
                            <input
                                type="date"
                                id="startDate"
                                className="form-control"
                                value={startDate.split('T')[0]}
                                onChange={handleStartDateChange}
                            />
                        </div>
                        <div className="col-sm-12 col-md-2">
                            <label htmlFor="endDate" className="form-label">{t('End Date')}</label>
                        </div>
                        <div className="col-sm-12 col-md-4">
                            <input
                                type="date"
                                id="endDate"
                                className="form-control"
                                value={endDate.split('T')[0]}
                                onChange={handleEndDateChange}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {salesReport && (
                <div className="row">
                    <div className="col-md-12">
                        <div className="card mb-4">
                            <div className="card-header bg-primary text-white">
                                <h5 className="m-0">{t('Summary')}</h5>
                            </div>
                            <div className="card-body">
                                {!salesReport.result ? (
                                    <div className="alert alert-warning">
                                        {t('No data available for the selected period')}
                                    </div>
                                ) : (
                                    <>
                                        <div className="row">
                                            <div className="col-sm-12 col-md-4 mb-3 mb-md-0">
                                                <div className="card bg-white border-0 shadow-sm h-100">
                                                    <div className="card-body text-center p-4">
                                                        <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 p-3 rounded-circle mb-3">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#0d6efd" className="bi bi-cart" viewBox="0 0 16 16">
                                                                <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM3.102 4l1.313 7h8.17l1.313-7H3.102zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                                                            </svg>
                                                        </div>
                                                        <h6 className="card-title text-secondary mb-2">{t('Total Sales')}</h6>
                                                        <h3 className="fw-bold text-primary">{salesReport.result.totalSalesCount || 0}</h3>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-sm-12 col-md-4 mb-3 mb-md-0">
                                                <div className="card bg-white border-0 shadow-sm h-100">
                                                    <div className="card-body text-center p-4">
                                                        <div className="d-inline-flex align-items-center justify-content-center bg-success bg-opacity-10 p-3 rounded-circle mb-3">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#198754" className="bi bi-currency-dollar" viewBox="0 0 16 16">
                                                                <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z"/>
                                                            </svg>
                                                        </div>
                                                        <h6 className="card-title text-secondary mb-2">{t('Total Sales Amount')}</h6>
                                                        <h3 className="fw-bold text-success">{formatCurrency(salesReport.result.totalSalesAmount || 0)}</h3>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-sm-12 col-md-4">
                                                <div className="card bg-white border-0 shadow-sm h-100">
                                                    <div className="card-body text-center p-4">
                                                        <div className="d-inline-flex align-items-center justify-content-center bg-warning bg-opacity-10 p-3 rounded-circle mb-3">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#ffc107" className="bi bi-trophy" viewBox="0 0 16 16">
                                                                <path d="M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5c0 .538-.012 1.05-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.5 13.11v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33.076 33.076 0 0 1 2.5.5zm.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935zm10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935zM3.504 1c.007.517.026 1.006.056 1.469.13 2.028.457 3.546.87 4.667C5.294 9.48 6.484 10 7 10a.5.5 0 0 1 .5.5v2.61a1 1 0 0 1-.757.97l-1.426.356a.5.5 0 0 0-.179.085L4.5 15h7l-.638-.479a.501.501 0 0 0-.18-.085l-1.425-.356a1 1 0 0 1-.757-.97V10.5A.5.5 0 0 1 9 10c.516 0 1.706-.52 2.57-2.864.413-1.12.74-2.64.87-4.667.03-.463.049-.952.056-1.469H3.504z"/>
                                                            </svg>
                                                        </div>
                                                        <h6 className="card-title text-secondary mb-2">{t('Total Commission')}</h6>
                                                        <h3 className="fw-bold text-warning">{formatCurrency(salesReport.result.totalCommissionAmount || 0)}</h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <div className="d-flex align-items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-calendar-check text-primary me-2" viewBox="0 0 16 16">
                                                    <path d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z"/>
                                                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                                                </svg>
                                                <p className="text-muted mb-0">
                                                    {t('Report Period')}: <span className="fw-bold text-dark">{salesReport.result.filterDateInfo || '-'}</span>
                                                </p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="card shadow-sm mt-4 border-0">
                            <div className="card-header bg-white border-bottom border-2 border-primary d-flex justify-content-between align-items-center py-3">
                                <h5 className="m-0 text-primary fw-bold">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-table me-2" viewBox="0 0 16 16">
                                        <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 2h-4v3h4V4zm0 4h-4v3h4V8zm0 4h-4v3h3a1 1 0 0 0 1-1v-2zm-5 3v-3H6v3h4zm-5 0v-3H1v2a1 1 0 0 0 1 1h3zm-4-4h4V8H1v3zm0-4h4V4H1v3zm5-3v3h4V4H6zm4 4H6v3h4V8z"/>
                                    </svg>
                                    {t('Sales Details')}
                                </h5>
                                <span className="badge bg-primary rounded-pill">{salesReport.result && salesReport.result.couponSales ? salesReport.result.couponSales.length : 0}</span>
                            </div>
                            <div className="card-body">
                                {!salesReport.result || !salesReport.result.couponSales || salesReport.result.couponSales.length === 0 ? (
                                    <div className="alert alert-info d-flex align-items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-info-circle-fill me-2 text-info" viewBox="0 0 16 16">
                                            <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
                                        </svg>
                                        <div>
                                            {t('No sales found for this period')}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover border-bottom">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="fw-semibold">{t('Date')}</th>
                                                    <th className="fw-semibold">{t('Coupon Code')}</th>
                                                    <th className="fw-semibold">{t('Course')}</th>
                                                    <th className="fw-semibold">{t('Sale Amount')}</th>
                                                    <th className="fw-semibold">{t('Commission Rate')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {salesReport.result.couponSales && salesReport.result.couponSales.map((sale, index) => {
                                                    console.log("Rendering sale item:", sale);
                                                    
                                                    // Try to access data from different possible structures
                                                    let saleDate = null;
                                                    let couponCode = null;
                                                    let courseName = null;
                                                    let saleAmount = null;
                                                    let commissionAmount = null;
                                                    
                                                    // Direct access
                                                    if (sale.saleDate) saleDate = sale.saleDate;
                                                    else if (sale.purchaseDate) saleDate = sale.purchaseDate;
                                                    
                                                    if (sale.couponCode) couponCode = sale.couponCode;
                                                    if (sale.courseName) courseName = sale.courseName;
                                                    
                                                    if (sale.saleAmount !== undefined) saleAmount = sale.saleAmount;
                                                    else if (sale.coursePrice !== undefined) saleAmount = sale.netAmount;
                                                    
                                                    if (sale.commissionAmount !== undefined) commissionAmount = sale.commissionAmount;
                                                    
                                                    // Look for nested array if user mentioned it
                                                    Object.keys(sale).forEach(key => {
                                                        if (Array.isArray(sale[key]) && sale[key].length > 0) {
                                                            console.log(`Found array in '${key}':`, sale[key]);
                                                            // Try to get data from the first item in the array
                                                            const nestedItem = sale[key][0];
                                                            if (nestedItem) {
                                                                if (!saleDate && nestedItem.saleDate) saleDate = nestedItem.saleDate;
                                                                else if (!saleDate && nestedItem.purchaseDate) saleDate = nestedItem.purchaseDate;
                                                                
                                                                if (!couponCode && nestedItem.couponCode) couponCode = nestedItem.couponCode;
                                                                if (!courseName && nestedItem.courseName) courseName = nestedItem.courseName;
                                                                
                                                                if (saleAmount === null && nestedItem.saleAmount !== undefined) saleAmount = nestedItem.saleAmount;
                                                                else if (saleAmount === null && nestedItem.coursePrice !== undefined) saleAmount = nestedItem.netAmount;
                                                                
                                                                if (commissionAmount === null && nestedItem.commissionAmount !== undefined) commissionAmount = nestedItem.commissionAmount;
                                                            }
                                                        }
                                                    });
                                                    
                                                    return (
                                                        <tr key={index} className="align-middle">
                                                            <td>
                                                                <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                                                    {formatDate(saleDate)}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="badge bg-warning bg-opacity-10 text-primary px-3 py-2">
                                                                    {couponCode || '-'}
                                                                </span>
                                                            </td>
                                                            <td className="fw-medium text-dark">{courseName || '-'}</td>
                                                            <td className="text-success fw-bold">{formatCurrency(saleAmount || 0)}</td>
                                                            <td className="text-warning fw-bold">%40</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
        <Footer/>
    </>
    );
};

export default InfluencerSalesReport;
